import { pubsub } from '@services';
import { getFocusableElements } from '@utils';
import { c as createFocusTrap } from './focus-trap.js';

/**
 * Drawer Usage:
 * Call the open(), close() or toggle() method to toggle the drawer
 *
 *
 * Drawer Toggle controllers:
 * Add these data attributes to any element to make it control a drawer:
 *
 * aria-controls="drawer-id"    // Required: unique ID of the drawer to control
 * aria-expanded="false"        // Required: 'true' or 'false'
 * data-drawer-action="toggle"  // Optional: 'open', 'close', or 'toggle' (default)
 *
 * Example:
 * <button aria-controls="cart-drawer" aria-expanded="false">Open Cart</button>
 */

class DrawerElement extends HTMLElement {
  static {
    // Let the document listen for clicks on drawer controllers
    document.addEventListener('click', (event) => {
      const controller = event.target.closest('[data-drawer-controller]');
      const drawerId = controller?.getAttribute('aria-controls');
      if (!drawerId) return;

      // Prevent default link behavior first
      event.preventDefault();

      let { drawerAction } = controller.dataset;
      const drawer = document.getElementById(drawerId);
      if (!drawer) {
        console.error(`Drawer with id '${drawerId}' not found`);
        return;
      }

      // If on search page and clicking search drawer controller, always open
      const isSearchDrawer = drawerId.includes('predictive-search-drawer');
      const isSearchPage = window.location.pathname.includes('/search');
      if (isSearchDrawer && isSearchPage && !drawerAction) {
        drawerAction = 'open';
      }

      drawer.handleAction(drawerAction);
    });
  }

  connectedCallback() {
    // Set the options
    const { subscribeEventName, subscribeEventAction } = this.dataset;
    this.options = { subscribeEventName,
      subscribeEventAction };
    this.focusOnOpenElement = null;

    // Get the modal body
    this.modalBody = this.querySelector('[data-drawer-body]');
    this.inert = this.ariaHidden !== 'false';
    this.previousActiveElement = null;
    if (!this.modalBody) return;

    // Listen for key:escape events
    this.unsubscribeEscapeKey = pubsub.subscribe('key:escape', () => this.close());

    // Create the focus trap
    this.trap = createFocusTrap(this.modalBody, {
      // Allow clicks outside the modal to close the drawer
      allowOutsideClick: true,
      initialFocus: false,
    });

    // Activate the trap if the drawer is rendered open
    if (this.ariaHidden === 'false') {
      this.trap.activate();
    }

    if (this.dataset.focusOnOpen) {
      const el = this.querySelector(`#${this.dataset.focusOnOpen}`);
      if (el) this.focusOnOpenElement = el;
    }

    // Subscribe to the event
    this.subscribeEvents();

    this.#handleShopifyEvents();
  }

  disconnectedCallback() {
    this.unsubscribeEscapeKey();
  }

  /**
   * Handle drawer actions
   * @param {string} action - The action to perform ('open', 'close', or 'toggle')
   */
  handleAction(action) {
    switch (action) {
    case 'open':
      this.open();
      break;
    case 'close':
      this.close();
      break;
    default:
      this.toggle();
      break;
    }
  }

  /**
   * Handle drawer controls
   */
  updateControllers() {
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((element) => {
      element.setAttribute('aria-expanded', this.ariaHidden === 'false' ? 'true' : 'false');
    });
  }

  getReturnFocusTarget() {
    if (
      this.previousActiveElement instanceof HTMLElement &&
      this.previousActiveElement.isConnected &&
      !this.contains(this.previousActiveElement)
    ) {
      return this.previousActiveElement;
    }

    const externalController = Array.from(
      document.querySelectorAll(`[data-drawer-controller][aria-controls="${this.id}"]`),
    ).find(element => !this.contains(element));

    return externalController instanceof HTMLElement ? externalController : null;
  }

  /**
   * Open the drawer
   */
  open() {
    const activeElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.previousActiveElement =
      activeElement && !this.contains(activeElement) ? activeElement : null;

    // Activate the focus trap after the transition ends
    const handleTransition = (event) => {
      if (event.target !== this.modalBody) return;
      this.trap.activate();
      this.modalBody.removeEventListener('transitionend', handleTransition);
    };
    // Only listen for transitionend when opening the drawer
    this.modalBody.addEventListener('transitionend', handleTransition);

    this.inert = false;
    this.ariaHidden = false;
    this.toggleBackdrop(false);
    this.updateControllers();

    if (this.focusOnOpenElement) {
      this.focusOnOpenElement.focus();
    }

    this.publishEvent('drawer:opened');
  }

  /**
   * Close the drawer
   */
  close() {
    // Pressing esc key triggers this function, even when the current drawer
    // isn't open. This creates overhead and will cause the incorrect target to
    // be focused (the aria-controler furthest down in the DOM).
    if (!this.trap.active) return;
    this.trap.deactivate({ returnFocus: false });
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && this.contains(activeElement)) {
      activeElement.blur();
    }

    const focusTarget = this.getReturnFocusTarget();

    setTimeout(() => {
      this.ariaHidden = true;
      this.inert = true;
      this.toggleBackdrop(true);
      this.updateControllers();
      this.publishEvent('drawer:closed');
      focusTarget?.focus?.({ preventScroll: true });
      this.previousActiveElement = null;
    }, 100);
  }

  /**
   * Toggle the drawer
   */
  toggle() {
    this.ariaHidden === 'true' ? this.open() : this.close();
  }

  /**
   * Toggle the backdrop
   * @param {boolean} hide - Whether to hide the backdrop
   */
  toggleBackdrop(hide) {
    this.backdrop = this.backdrop || this.querySelector('[data-backdrop]');
    this.backdrop.ariaHidden = hide;
  }

  /**
   * Update the trap container content elements, always add the header and footer.
   * @param {HTMLElement} element - The new element to trap focus on
   */
  updateTrapContainerElements(element) {
    this.drawerHeader = this.drawerHeader || this.querySelector('.drawer__header');
    this.drawerFooter = this.drawerFooter || this.querySelector('.drawer__footer');
    const elementsArray = [this.drawerHeader, element, this.drawerFooter].filter(Boolean); // Remove null/undefined elements

    this.trap.updateContainerElements(elementsArray);

    // Get focusable elements from each container and combine them
    const focusableElements = elementsArray.flatMap(el => getFocusableElements(el));

    // Focus the first focusable element if it exists
    focusableElements?.[0]?.focus();
  }

  /**
   * Publish the event
   */
  publishEvent(eventName) {
    pubsub.publish(`${eventName}:${this.id}`, {
      target: this.id,
    });
  }

  /**
   * Subscribe to the event
   */
  subscribeEvents() {
    if (!this.options.subscribeEventName || !this.options.subscribeEventAction) return;

    pubsub.subscribe(this.options.subscribeEventName, () => {
      this.handleAction(this.options.subscribeEventAction);
    });
  }

  /**
   * Handle block select events
   */
  #handleShopifyEvents() {
    const events = [
      'shopify:section:load',
      'shopify:section:select',
      'shopify:section:unload',
      'shopify:section:deselect',
    ];

    events.forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        const { target, type } = event;
        const drawer = target.querySelector('drawer-element');

        switch (type) {
        case 'shopify:section:load':
        case 'shopify:section:select':
          drawer?.open();
          break;
        case 'shopify:section:unload':
        case 'shopify:section:deselect':
          drawer?.close();
          break;
        }
      });
    });
  }
}

customElements.define('drawer-element', DrawerElement);

export { DrawerElement };
